/* eslint-disable @typescript-eslint/no-unused-vars */
import { useNavigate } from "react-router-dom";
import AddItemButton from "../../Components/AddItemButton";
import Layout from "../../Components/Layout";
import React, { useEffect, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Metatags, WrapTextCellProps } from "../../types";
import { BASE_API_URL } from "../../API/Base";
import AxiosInstance from "../../API/axios";
import { usePagePermissions } from "../../Components/PagePermission";
import { Delete, EditNote, MenuBook } from "@mui/icons-material";
import toast from "react-hot-toast";
import DeleteModal from "../../Modals/DeleteModal";

type Props = {};

const Metamaster = (props: Props) => {
  const [rows, setRows] = useState<Metatags[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [rowCount, setRowCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(30);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const permissions = usePagePermissions(12);
  const navigate = useNavigate();

  const fetchData = async (
    page: number,
    pageSize: number,
    searchValue: string
  ) => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get(
        `${BASE_API_URL}/admin/web-content/v1/get-all-metatags`,
        {
          params: {
            page,
            per_page: pageSize,
            search_query: searchValue,
          },
        }
      );

      if (
        !response.data ||
        !response.data.data ||
        response.data.total_count == null
      ) {
        console.error("Invalid response format:", response.data);
        setLoading(false);
        return;
      }

      const formattedData = response.data.data.map(
        (row: Metatags, index: number) => ({
          ...row,
          row: row.id,
          actionsID: startIndex + index + 1,
        })
      );

      setRows(formattedData);
      setRowCount(response.data.total_count);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, debouncedSearchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchValue]);

  // Calculate start and end index for the visible rows
  const startIndex = (currentPage - 1) * pageSize;

  const base64Encode = (str: string) => {
    return btoa(str);
  };
  const base64Decode = (encodedStr: string) => {
    return atob(encodedStr);
  };

  const handleEdit = (row: any) => {
    const encodedId = base64Encode(row.id);
    navigate(`/web/meta-tags/form/edit/${encodedId}`);
  };

  const handleDelete = (row: any) => {
    const encodedId = base64Encode(row.id);
    setIsDeleteModalOpen(true);
    setSelectedRowId(encodedId);
  };

  const WrapTextCell: React.FC<WrapTextCellProps> = ({ value }) => {
    return (
      <div
        style={{
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          maxWidth: "390px",
        }}
      >
        {value}
      </div>
    );
  };

  const handleAdd = () => {
    navigate("/web/meta-tags/form/create");
  };

  const handleView = (row: any) => {
    const encodedId = base64Encode(row.id);

    navigate(`/web/meta-tags/form/view/${encodedId}`);
  };

  const handleDeleteConfirm = async () => {
    try {
      const decodedId = selectedRowId ? base64Decode(selectedRowId) : null;

      const response = await AxiosInstance.delete(
        `${BASE_API_URL}/admin/web-content/v1/delete-metadata/${decodedId}`
      );

      if (response.data) {
        // Remove the deleted row from the local state
        setIsDeleteModalOpen(false);
        setRows((prevRows) =>
          prevRows.filter((row: any) => row.id.toString() !== decodedId)
        );
        toast.success("Data deleted successfully");
      } else {
        toast.error("Error deleting data");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      setIsDeleteModalOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const actionColumn =
    permissions?.Update || permissions?.Delete || permissions?.View
      ? {
          field: "action",
          headerName: "Action",
          width: 120,
          renderCell: (params: any) => (
            <div className="flex flex-row gap-x-1">
              {permissions?.Update && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Edit cursor-pointer"
                  onClick={() => {
                    handleEdit(params.row);
                  }}
                >
                  <Tooltip title="Edit">
                    <EditNote sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.Delete && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-Delete cursor-pointer"
                  onClick={() => {
                    handleDelete(params.row);
                    const actionId = permissions?.Delete?.id;
                  }}
                >
                  <Tooltip title="Delete">
                    <Delete sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
              {permissions?.View && (
                <div
                  className="px-1.5 py-0.5 rounded-md text-View cursor-pointer flex flex-row items-center justify-center"
                  onClick={() => {
                    handleView(params.row);
                    const actionId = permissions?.View?.id;
                  }}
                >
                  <Tooltip title="View">
                    <MenuBook sx={{ fontSize: "20px" }} />
                  </Tooltip>
                </div>
              )}
            </div>
          ),
        }
      : undefined;

  const coloumns: GridColDef[] = [
    {
      field: "actionsID",
      headerName: "ID",

      width: 70,
      renderCell: (params) => (
        <div className="cellWithStatus">{params.value}</div>
      ),
    },
    ...(actionColumn ? [actionColumn] : []),
    {
      field: "meta_title",
      headerName: "Title",
      cellClassName: "capitalize",
      width: 300,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },

    {
      field: "meta_description",
      headerName: "Description",
      cellClassName: "capitalize",
      width: 300,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },

    {
      field: "meta_keywords",
      headerName: "Keywords",
      cellClassName: "capitalize",
      width: 450,
      renderCell: (params) => (
        <div className={`cellWithStatus ${params.value ? "" : "inactive"}`}>
          {params.value ? <WrapTextCell value={params.value} /> : "N/A"}
        </div>
      ),
    },
  ];

  return (
    <Layout PageId={12}>
      <div className="flex flex-col items-center justify-start w-full h-full overflow-hidden">
        <div
          id="actions"
          className="flex flex-col items-center justify-between w-full md:flex-row gap-x-2"
        >
          <div className="flex flex-row items-center justify-center w-full md:w-1/4 "></div>
          <div className="flex items-center justify-between w-full md:justify-end md:space-x-2 ">
            <AddItemButton
              label="Add metatags"
              onClick={handleAdd}
              size="medium"
            />
          </div>
        </div>
        <div className="flex flex-col items-start justify-start w-full mt-2">
          <div className="w-full">
            <Box>
              <DataGrid
                rows={rows}
                columns={coloumns}
                getRowId={(row) => row.id}
                autoHeight
                loading={loading}
              />
            </Box>
          </div>
        </div>
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Confirm Deletion"
          message="Are you sure you want to delete this data?"
        />
      </div>
    </Layout>
  );
};

export default Metamaster;
